import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const COMPANY_DOC_CATEGORIES = [
  'kyc',
  'insurance_license',
  'contract',
  'registration_card',
  'inspection',
  'driver_license',
  'loading_photos',
  'delivery_photos',
  'cmr',
  'other',
] as const;

export type CompanyDocCategory = (typeof COMPANY_DOC_CATEGORIES)[number];

export interface CompanyDocument {
  id: string;
  company_id: string;
  order_id: string | null;
  vehicle_id: string | null;
  driver_id: string | null;
  category: string;
  title: string | null;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
  expiry_date: string | null;
  notes: string | null;
  uploaded_by: string | null;
  uploaded_by_admin: boolean;
  created_at: string;
}

export const COMPANY_DOCS_BUCKET = 'company-documents';

export interface UploadCompanyDocumentInput {
  companyId: string;
  orderId?: string | null;
  vehicleId?: string | null;
  driverId?: string | null;
  category: CompanyDocCategory;
  title?: string;
  expiryDate?: string | null;
  notes?: string;
  file: File;
}

export const useCompanyDocuments = (companyId?: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['company-documents', companyId ?? 'all'],
    queryFn: async (): Promise<CompanyDocument[]> => {
      let req = supabase
        .from('company_documents')
        .select('*')
        .order('created_at', { ascending: false });
      if (companyId) req = req.eq('company_id', companyId);
      const { data, error } = await req;
      if (error) throw error;
      return (data || []) as CompanyDocument[];
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['company-documents'] });
  };

  const uploadMutation = useMutation({
    mutationFn: async (input: UploadCompanyDocumentInput) => {
      const ext = input.file.name.split('.').pop() || 'bin';
      const path = `${input.companyId}/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(COMPANY_DOCS_BUCKET)
        .upload(path, input.file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase.from('company_documents').insert({
        company_id: input.companyId,
        order_id: input.orderId || null,
        vehicle_id: input.vehicleId || null,
        driver_id: input.driverId || null,
        category: input.category,
        title: input.title?.trim() || null,
        file_name: input.file.name,
        file_url: path,
        file_type: input.file.type || 'application/octet-stream',
        file_size: input.file.size,
        expiry_date: input.expiryDate || null,
        notes: input.notes?.trim() || null,
        uploaded_by: user?.id ?? null,
        uploaded_by_admin: true,
      });

      if (dbError) {
        await supabase.storage.from(COMPANY_DOCS_BUCKET).remove([path]);
        throw dbError;
      }
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (doc: CompanyDocument) => {
      const { error } = await supabase.from('company_documents').delete().eq('id', doc.id);
      if (error) throw error;
      await supabase.storage.from(COMPANY_DOCS_BUCKET).remove([doc.file_url]);
    },
    onSuccess: invalidate,
  });

  return {
    documents: query.data || [],
    isLoading: query.isLoading,
    refetch: query.refetch,
    uploadDocument: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    deleteDocument: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};
